import { activateCheckInFlowResultSchema, checkInFieldDTOSchema, checkInFlowDetailDTOSchema, checkInFlowListItemDTOSchema, flowPreviewDTOSchema, } from '@instanexus/contracts';
import { z } from 'zod';
import { dataEnvelopeSchema, listEnvelopeSchema, WEB_API_PREFIX } from '../transport.js';
const flowListSchema = listEnvelopeSchema(checkInFlowListItemDTOSchema);
const flowDetailSchema = dataEnvelopeSchema(checkInFlowDetailDTOSchema);
const flowPreviewSchema = dataEnvelopeSchema(flowPreviewDTOSchema);
const flowActivationSchema = dataEnvelopeSchema(activateCheckInFlowResultSchema);
const fieldListSchema = listEnvelopeSchema(checkInFieldDTOSchema);
const singleFieldSchema = dataEnvelopeSchema(checkInFieldDTOSchema);
export function createFlowsResource(transport) {
    return {
        async list(query) {
            return transport.request({
                path: `${WEB_API_PREFIX}/flows`,
                schema: flowListSchema,
                query: {
                    page: query?.page,
                    pageSize: query?.pageSize,
                    sort: query?.sort,
                    scope: query?.scope,
                    locationId: query?.locationId,
                    visitorTypeId: query?.visitorTypeId,
                    isActive: query?.isActive,
                },
            });
        },
        async create(input) {
            const envelope = await transport.request({
                method: 'POST',
                path: `${WEB_API_PREFIX}/flows`,
                schema: flowDetailSchema,
                body: input,
            });
            return envelope.data;
        },
        async duplicate(id, input) {
            const envelope = await transport.request({
                method: 'POST',
                path: `${WEB_API_PREFIX}/flows/${encodeURIComponent(id)}/duplicate`,
                schema: flowDetailSchema,
                body: input,
            });
            return envelope.data;
        },
        async remove(id) {
            await transport.request({
                method: 'DELETE',
                path: `${WEB_API_PREFIX}/flows/${encodeURIComponent(id)}`,
                schema: z.undefined(),
            });
        },
        async get(id) {
            const envelope = await transport.request({
                path: `${WEB_API_PREFIX}/flows/${encodeURIComponent(id)}`,
                schema: flowDetailSchema,
            });
            return envelope.data;
        },
        async updateFlow(id, input) {
            const envelope = await transport.request({
                method: 'PATCH',
                path: `${WEB_API_PREFIX}/flows/${encodeURIComponent(id)}`,
                schema: flowDetailSchema,
                body: input,
            });
            return envelope.data;
        },
        async activateFlow(id) {
            const envelope = await transport.request({
                method: 'POST',
                path: `${WEB_API_PREFIX}/flows/${encodeURIComponent(id)}/activate`,
                schema: flowActivationSchema,
            });
            return envelope.data;
        },
        async preview(id, query) {
            const envelope = await transport.request({
                path: `${WEB_API_PREFIX}/flows/${encodeURIComponent(id)}/preview`,
                schema: flowPreviewSchema,
                query: {
                    locationId: query?.locationId,
                    locale: query?.locale,
                    visitorTypeId: query?.visitorTypeId,
                },
            });
            return envelope.data;
        },
        async listFields(query) {
            return transport.request({
                path: `${WEB_API_PREFIX}/fields`,
                schema: fieldListSchema,
                query: { page: query?.page, pageSize: query?.pageSize, fieldType: query?.fieldType, isSystem: query?.isSystem },
            });
        },
        async createField(input) {
            const envelope = await transport.request({
                method: 'POST',
                path: `${WEB_API_PREFIX}/fields`,
                schema: singleFieldSchema,
                body: input,
            });
            return envelope.data;
        },
        async updateField(id, input) {
            const envelope = await transport.request({
                method: 'PATCH',
                path: `${WEB_API_PREFIX}/fields/${encodeURIComponent(id)}`,
                schema: singleFieldSchema,
                body: input,
            });
            return envelope.data;
        },
        async regenerateFieldTranslations(id, input) {
            const envelope = await transport.request({
                method: 'POST',
                path: `${WEB_API_PREFIX}/fields/${encodeURIComponent(id)}/translations`,
                schema: singleFieldSchema,
                body: input,
            });
            return envelope.data;
        },
        async removeField(id) {
            await transport.request({
                method: 'DELETE',
                path: `${WEB_API_PREFIX}/fields/${encodeURIComponent(id)}`,
                schema: z.undefined(),
            });
        },
        async addScreenField(flowId, screenId, input) {
            const envelope = await transport.request({
                method: 'POST',
                path: `${WEB_API_PREFIX}/flows/${encodeURIComponent(flowId)}/screens/${encodeURIComponent(screenId)}/fields`,
                schema: flowDetailSchema,
                body: input,
            });
            return envelope.data;
        },
        async reorderScreenFields(flowId, screenId, input) {
            const envelope = await transport.request({
                method: 'PATCH',
                path: `${WEB_API_PREFIX}/flows/${encodeURIComponent(flowId)}/screens/${encodeURIComponent(screenId)}/fields/reorder`,
                schema: flowDetailSchema,
                body: input,
            });
            return envelope.data;
        },
        async updateScreenField(flowId, screenId, screenFieldId, input) {
            const envelope = await transport.request({
                method: 'PATCH',
                path: `${WEB_API_PREFIX}/flows/${encodeURIComponent(flowId)}/screens/${encodeURIComponent(screenId)}/fields/${encodeURIComponent(screenFieldId)}`,
                schema: flowDetailSchema,
                body: input,
            });
            return envelope.data;
        },
        async removeScreenField(flowId, screenId, screenFieldId) {
            await transport.request({
                method: 'DELETE',
                path: `${WEB_API_PREFIX}/flows/${encodeURIComponent(flowId)}/screens/${encodeURIComponent(screenId)}/fields/${encodeURIComponent(screenFieldId)}`,
                schema: z.undefined(),
            });
        },
        async addScreen(flowId, input) {
            const envelope = await transport.request({
                method: 'POST',
                path: `${WEB_API_PREFIX}/flows/${encodeURIComponent(flowId)}/screens`,
                schema: flowDetailSchema,
                body: input,
            });
            return envelope.data;
        },
        async reorderScreens(flowId, input) {
            const envelope = await transport.request({
                method: 'PATCH',
                path: `${WEB_API_PREFIX}/flows/${encodeURIComponent(flowId)}/screens/reorder`,
                schema: flowDetailSchema,
                body: input,
            });
            return envelope.data;
        },
        async updateScreen(flowId, screenId, input) {
            const envelope = await transport.request({
                method: 'PATCH',
                path: `${WEB_API_PREFIX}/flows/${encodeURIComponent(flowId)}/screens/${encodeURIComponent(screenId)}`,
                schema: flowDetailSchema,
                body: input,
            });
            return envelope.data;
        },
        async removeScreen(flowId, screenId) {
            await transport.request({
                method: 'DELETE',
                path: `${WEB_API_PREFIX}/flows/${encodeURIComponent(flowId)}/screens/${encodeURIComponent(screenId)}`,
                schema: z.undefined(),
            });
        },
    };
}
//# sourceMappingURL=flows.js.map